import type { GuideTourStep } from '../types'
import { GUIDE_TOUR_PLACEMENTS } from '../types'
import { assertNonEmptyString } from './assertions'
import { GuideTourValidationError } from './errors'
import { validateOptionalGuideTourExpiresAt } from './expiresAt'
import { validateGuideTourObserverSelectors } from './observerSelectors'
import { validateGuideTourWhenTargetMissing } from './whenTargetMissing'

const validateGuideTourStepModalVisible = (
  waitForModalVisible: GuideTourStep['waitForModalVisible'],
  prefix: string
): void => {
  if (waitForModalVisible === undefined) {
    return
  }

  const isValid =
    waitForModalVisible === true ||
    waitForModalVisible === false ||
    (typeof waitForModalVisible === 'string' &&
      waitForModalVisible.trim().length > 0)

  if (!isValid) {
    throw new GuideTourValidationError(
      `${prefix}.waitForModalVisible must be true, false, or a non-empty modal id`
    )
  }
}

const validateOptionalBooleanField = (
  value: unknown,
  fieldName: string
): void => {
  if (value !== undefined && typeof value !== 'boolean') {
    throw new GuideTourValidationError(`${fieldName} must be a boolean`)
  }
}

export const validateGuideTourStepFields = (
  step: GuideTourStep,
  stepIndex: number
): void => {
  const prefix = `steps[${stepIndex}]`

  assertNonEmptyString(step.title, `${prefix}.title`)
  assertNonEmptyString(step.body, `${prefix}.body`)

  if (step.targetId !== undefined) {
    assertNonEmptyString(step.targetId, `${prefix}.targetId`)
  }

  if (step.badge !== undefined) {
    assertNonEmptyString(step.badge, `${prefix}.badge`)
  }

  if (step.imageUrl !== undefined) {
    assertNonEmptyString(step.imageUrl, `${prefix}.imageUrl`)
  }

  if (step.route !== undefined) {
    assertNonEmptyString(step.route, `${prefix}.route`)
  }

  if (
    step.placement !== undefined &&
    !GUIDE_TOUR_PLACEMENTS.includes(step.placement)
  ) {
    throw new GuideTourValidationError(
      `${prefix}.placement must be one of: ${GUIDE_TOUR_PLACEMENTS.join(', ')}`
    )
  }

  validateGuideTourObserverSelectors(
    step.waitForHidden,
    `${prefix}.waitForHidden`
  )

  validateOptionalBooleanField(
    step.waitForModalHidden,
    `${prefix}.waitForModalHidden`
  )

  validateGuideTourStepModalVisible(step.waitForModalVisible, prefix)

  if (step.forceClick !== undefined) {
    assertNonEmptyString(step.forceClick, `${prefix}.forceClick`)
  }

  if (step.scrollIntoView !== undefined) {
    assertNonEmptyString(step.scrollIntoView, `${prefix}.scrollIntoView`)
  }

  validateOptionalBooleanField(
    step.autoScrollToTarget,
    `${prefix}.autoScrollToTarget`
  )

  validateOptionalBooleanField(
    step.allowTargetInteraction,
    `${prefix}.allowTargetInteraction`
  )

  validateOptionalGuideTourExpiresAt(step.expiresAt, `${prefix}.expiresAt`)
  validateGuideTourWhenTargetMissing(step, prefix)
}
